import React, { useEffect, useState, useRef } from 'react';
import {
  getUserSubscriptions,
  cancelSubscription,
} from '../../services/subscriptionService';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import Cookies from 'js-cookie';
import {
  FaCreditCard,
  FaCalendarAlt,
  FaTimes,
  FaCrown,
  FaCheckCircle,
  FaExclamationTriangle,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import { useToastMessages } from '../../components/MessageToast/MessageToast';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import apiClient from '../../services/apiClient';

const MySubscriptions = ({ setIsAuthenticated }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const isMountedRef = useRef(true);
  const navigate = useNavigate();

  // Cleanup când componenta se demontează
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Toast messages hook
  const { showSuccess, showError, ToastContainer } = useToastMessages();

  const [openingPortal, setOpeningPortal] = useState(false);
  const handleOpenCustomerPortal = async () => {
    setOpeningPortal(true);
    try {
      const res = await apiClient.post('/api/subscription/customer-portal');
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        showError('Nu am putut deschide portalul Stripe.');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Eroare la deschiderea portalului Stripe.';
      showError(msg);
    } finally {
      setOpeningPortal(false);
    }
  };

  // Dialog state pentru confirmarea anulării
  const [dialogConfig, setDialogConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: null,
    confirmText: 'Confirmă',
    cancelText: 'Anulează',
  });

  const showConfirmDialog = (
    title,
    message,
    onConfirm,
    type = 'warning',
    confirmText = 'Confirmă',
  ) => {
    setDialogConfig({
      isOpen: true,
      title,
      message,
      type,
      onConfirm: () => {
        setDialogConfig((prev) => ({ ...prev, isOpen: false }));
        onConfirm();
      },
      confirmText,
      cancelText: 'Anulează',
    });
  };

  const closeDialog = () => {
    setDialogConfig((prev) => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    // Previne apelurile multiple simultane
    if (isLoading) {
      return;
    }

    const fetchSubscriptions = async () => {
      setIsLoading(true);
      try {
        const data = await getUserSubscriptions();

        // Verifică dacă componenta este încă montată înainte de a actualiza state-ul
        if (!isMountedRef.current) return;

        // Filtrează doar subscripțiile active (enabled = true)
        const activeSubscriptions = data.filter((sub) => sub.enable !== false);

        setSubscriptions(
          activeSubscriptions.length > 0
            ? activeSubscriptions
            : [
                {
                  id: 'freemium',
                  planName: 'Abonament Freemium',
                  description: 'Acces gratuit la funcționalitățile de bază.',
                  paidAmount: '0',
                  currency: 'RON',
                  startDate: new Date(),
                  endDate: new Date(),
                  enable: true,
                },
              ],
        );
      } catch (err) {
        // Verifică dacă componenta este încă montată
        if (!isMountedRef.current) return;

        // Folosim directe showError fără să-l punem în dependency array
        // pentru a evita bucla infinită
        showError('Eroare la încărcarea abonamentelor. Încearcă din nou.');
        console.error(err);
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intenționat gol pentru a se executa doar o dată

  const handleRefund = async (subscriptionId) => {
    if (subscriptionId === 'freemium') return;

    // Găsește abonamentul pentru a afișa detalii în confirmarea de anulare
    const subscription = subscriptions.find((sub) => sub.id === subscriptionId);
    const subscriptionName = subscription
      ? subscription.planName
      : 'Abonamentul';
    const subscriptionAmount = subscription
      ? `${subscription.currency} ${subscription.paidAmount}`
      : '';

    showConfirmDialog(
      'Confirmare Anulare Abonament',
      `Sunteți absolut sigur că doriți să anulați "${subscriptionName}"?\n\n` +
        `💰 Valoare: ${subscriptionAmount}/lună\n` +
        `📅 Data expirare: ${subscription ? format(new Date(subscription.endDate), 'dd MMM yyyy') : ''}\n\n` +
        `⚠️ ATENȚIE: Această acțiune va:\n` +
        `• Anula imediat abonamentul\n` +
        `• Vă va deconecta din cont\n` +
        `• Restrict accesul la funcționalitățile premium\n\n` +
        `Acțiunea nu poate fi anulată!`,
      async () => {
        const token = Cookies.get('jwtToken');
        const userId = Cookies.get('userId');

        try {
          if (!userId) {
            throw new Error('User ID lipsește din cookies');
          }
          if (!token) {
            throw new Error('JWT Token lipsește din cookies');
          }

          await cancelSubscription(userId, token);
          showSuccess('Abonamentul a fost anulat cu succes!');

          // Actualizează lista de subscripții cu abonamentul freemium
          setSubscriptions([
            {
              id: 'freemium',
              planName: 'Abonament Freemium',
              description: 'Acces gratuit la funcționalitățile de bază.',
              paidAmount: '0',
              currency: 'RON',
              startDate: new Date(),
              endDate: new Date(),
              enable: true,
            },
          ]);

          // Curățare cookies
          Cookies.remove('jwtToken');
          Cookies.remove('userId');
          Cookies.remove('user');
          Cookies.remove('subscriptionType');

          // Verifică dacă setIsAuthenticated este o funcție validă
          if (typeof setIsAuthenticated === 'function') {
            setIsAuthenticated(false);
          }

          // Redirectează către login după o scurtă întârziere
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        } catch (err) {
          console.error('Error canceling subscription:', err);
          showError(`Anularea abonamentului a eșuat: ${err.message}`);
        }
      },
      'error',
      'Da, Anulează Definitiv',
    );
  };

  const getPlanIcon = (planName) => {
    const safePlanName = planName?.toLowerCase() || '';
    if (safePlanName.includes('freemium') || safePlanName.includes('free')) {
      return <FaCheckCircle className="text-green-500" />;
    }
    return <FaCrown className="text-yellow-500" />;
  };

  const getPlanColor = (planName) => {
    const safePlanName = planName?.toLowerCase() || '';
    if (safePlanName.includes('freemium') || safePlanName.includes('free')) {
      return 'from-green-50 to-emerald-50 border-green-200';
    }
    return 'from-yellow-50 to-orange-50 border-yellow-200';
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-8 rounded-2xl shadow-lg border border-purple-100">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-purple-500 text-white flex items-center justify-center rounded-xl shadow-lg">
            <FaCreditCard className="text-xl" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              Abonamentul Meu
            </h2>
            <p className="text-gray-600">
              Gestionează planul și facturarea contului
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {subscriptions.map((subscription) => (
            <div
              key={subscription.id}
              className={`bg-gradient-to-r ${getPlanColor(subscription.planName)} p-6 rounded-xl shadow-sm border`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-2xl">
                    {getPlanIcon(subscription.planName)}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                      {subscription.planName || 'Plan Necunoscut'}
                      {subscription.id === 'freemium' && (
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                          GRATUIT
                        </span>
                      )}
                    </h3>
                    <p className="text-gray-600 mt-1">
                      {subscription.description || 'Descriere indisponibilă'}
                    </p>
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-800">
                    {subscription.currency || 'RON'}{' '}
                    {subscription.paidAmount || 0}
                    {subscription.id !== 'freemium' && (
                      <span className="text-sm font-normal">/lună</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-white bg-opacity-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <FaCalendarAlt className="text-indigo-500" />
                    <span className="font-semibold text-gray-700">
                      Data Început
                    </span>
                  </div>
                  <p className="text-gray-800 font-medium">
                    {subscription.startDate
                      ? format(new Date(subscription.startDate), 'dd MMM yyyy')
                      : 'Data necunoscută'}
                  </p>
                </div>

                <div className="bg-white bg-opacity-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <FaCalendarAlt className="text-indigo-500" />
                    <span className="font-semibold text-gray-700">
                      Data Expirare
                    </span>
                  </div>
                  <p className="text-gray-800 font-medium">
                    {subscription.endDate
                      ? format(new Date(subscription.endDate), 'dd MMM yyyy')
                      : 'Data necunoscută'}
                  </p>
                </div>
              </div>

              {subscription.id !== 'freemium' && (
                <div className="pt-4 border-t border-white border-opacity-50">
                  {subscription.endDate &&
                  new Date(subscription.endDate) <= new Date() ? (
                    <div className="flex items-center gap-2 text-red-600">
                      <FaExclamationTriangle />
                      <span className="font-medium">Abonamentul a expirat</span>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-medium transition-all shadow-lg disabled:opacity-50"
                        onClick={handleOpenCustomerPortal}
                        disabled={openingPortal}
                        title="Schimba cardul, vezi facturile, schimba planul"
                      >
                        <FaExternalLinkAlt />
                        {openingPortal ? 'Se deschide...' : 'Gestioneaza in Stripe'}
                      </button>
                      <button
                        className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-medium transition-all transform hover:scale-105 shadow-lg"
                        onClick={() => handleRefund(subscription.id)}
                      >
                        <FaTimes />
                        Anulează Abonamentul
                      </button>
                    </div>
                  )}
                </div>
              )}

              {subscription.id === 'freemium' && (
                <div className="pt-4 border-t border-white border-opacity-50">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <FaCrown className="text-blue-500" />
                      <span className="font-semibold text-blue-800">
                        Upgrade la Premium
                      </span>
                    </div>
                    <p className="text-blue-700 text-sm mb-3">
                      Accesează funcționalități avansate, rapoarte detaliate și
                      alertele nelimitate.
                    </p>
                    <button className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition-colors">
                      Vezi Planurile Premium
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Dialog pentru confirmarea anulării */}
      <ConfirmDialog
        isOpen={dialogConfig.isOpen}
        onClose={closeDialog}
        onConfirm={dialogConfig.onConfirm}
        title={dialogConfig.title}
        message={dialogConfig.message}
        type={dialogConfig.type}
        confirmText={dialogConfig.confirmText}
        cancelText={dialogConfig.cancelText}
      />

      <ToastContainer />
    </div>
  );
};

export default MySubscriptions;
